import { collection, doc, runTransaction, Timestamp } from "firebase/firestore";
import { AiResponse, AiResponseStatus, AiResponseType } from "../models/interfaces/ai-response";
import { Organization } from "../models/interfaces/organization";
import { buildPartialMskScore } from "./msk-mapping";
import getFirebase from "./firebase";
import { MskScore } from "../models/interfaces/scores/msk-score";
import i18n from "../i18n";
import { createLogger } from "./logger";

const log = createLogger("AiResponseUtils");

const { db } = getFirebase();

const MSK_SCORES_COLLECTION = "mskScores";
const AI_RESPONSES_COLLECTION = "aiResponses";
const ORGANIZATIONS_COLLECTION = "organizations";

export const scheduleAiMskScoreSummary = async (
    mskScore: MskScore,
    organizationId?: string,
): Promise<AiResponse> => {
    const input = buildPartialMskScore(mskScore);

    const mskScoreId = mskScore.id;
    if (!mskScoreId) {
        throw new Error("mskScore.id is required to create an aiResponse");
    }

    const aiResponseId = doc(collection(db, AI_RESPONSES_COLLECTION)).id;
    const now = Timestamp.now();
    let aiResponse: AiResponse | undefined;

    await runTransaction(db, async (transaction) => {
        const mskScoreRef = doc(db, MSK_SCORES_COLLECTION, mskScoreId);

        const mskScoreSnapshot = await transaction.get(mskScoreRef);
        if (!mskScoreSnapshot.exists()) {
            throw new Error(`mskScore ${mskScoreId} not found`);
        }

        // AI summary limits and counts live on the head organization, so
        // responses generated under a sub-organization belong to its parent.
        let resolvedOrganizationId = organizationId;
        if (organizationId) {
            const organizationSnapshot = await transaction.get(doc(db, ORGANIZATIONS_COLLECTION, organizationId));
            if (organizationSnapshot.exists()) {
                const organization = organizationSnapshot.data() as Organization;
                resolvedOrganizationId = organization.parentOrganizationId ?? organizationId;
                if (resolvedOrganizationId !== organizationId) {
                    log.debug("AI summary response attributed to head organization", {
                        organizationId,
                        resolvedOrganizationId,
                    });
                }
            }
        }

        aiResponse = {
            id: aiResponseId,
            type: AiResponseType.MskScoreSummary,
            status: AiResponseStatus.Scheduled,
            organizationId: resolvedOrganizationId,
            input: input as unknown as Record<string, any>,
            output: undefined,
            outputResponseLanguage: i18n.language,
            agentName: "",
            agentVersion: "",
            modelDeployment: undefined,
            responseId: undefined,
            inputTokens: undefined,
            outputTokens: undefined,
            totalTokens: undefined,
            latencyMs: undefined,
            retryCount: 0,
            error: undefined,
            isLiked: undefined,
            createdBy: undefined,
            updatedBy: undefined,
            created: now,
            updated: now,
        };

        const aiResponseRef = doc(db, AI_RESPONSES_COLLECTION, aiResponseId);
        transaction.set(aiResponseRef, aiResponse);

        transaction.update(mskScoreRef, {
            aiResponseId,
            updated: now,
        });
    });

    if (!aiResponse) {
        throw new Error("aiResponse was not created");
    }

    return aiResponse;
};
